import { lazy, Suspense, useEffect } from "react";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { startAnalytics, conversionEventForHref, trackEvent } from "@/lib/analytics";
import { MobileBookingBar } from "@/components/mobile-booking-bar";

const NotFound = lazy(() => import("@/pages/not-found"));
const Home = lazy(() => import("@/pages/home"));
const Donate = lazy(() => import("@/pages/donate"));
const Sponsor = lazy(() => import("@/pages/sponsor"));
const Booking = lazy(() => import("@/pages/booking"));
const Admin = lazy(() => import("@/pages/admin"));
const CaregiverGuide = lazy(() => import("@/pages/caregiver-guide"));
const VolunteerGuide = lazy(() => import("@/pages/volunteer-guide"));
const RiderGuide = lazy(() => import("@/pages/rider-guide"));
const Accessibility = lazy(() => import("@/pages/accessibility"));
const Guides = lazy(() => import("@/pages/guides"));
const About = lazy(() => import("@/pages/about"));
const Contact = lazy(() => import("@/pages/contact"));
const Impact = lazy(() => import("@/pages/impact"));

function Router() {
  return (
    <Suspense
      fallback={
        <main className="min-h-[60vh] bg-background px-6 py-24">
          <div className="mx-auto max-w-3xl animate-pulse rounded-2xl bg-secondary/40 p-12 text-center text-muted-foreground">
            Loading…
          </div>
        </main>
      }
    >
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/donate" component={Donate} />
        <Route path="/sponsor" component={Sponsor} />
        <Route path="/book" component={Booking} />
        <Route path="/resources/caregiver" component={CaregiverGuide} />
        <Route path="/resources/volunteer" component={VolunteerGuide} />
        <Route path="/resources/rider" component={RiderGuide} />
        <Route path="/guides/caregiver" component={LegacyCaregiverGuideRedirect} />
        <Route path="/guides/volunteer" component={LegacyVolunteerGuideRedirect} />
        <Route path="/guides/rider" component={LegacyRiderGuideRedirect} />
        <Route path="/guides" component={LegacyGuidesRedirect} />
        <Route path="/accessibility" component={Accessibility} />
        <Route path="/resources" component={Guides} />
        <Route path="/about" component={About} />
        <Route path="/contact" component={Contact} />
        <Route path="/impact" component={Impact} />
        <Route path="/commandcenter" component={Admin} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function LegacyGuidesRedirect() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    setLocation("/resources");
  }, [setLocation]);

  return null;
}

function LegacyResourceRedirect({ to }: { to: string }) {
  const [, setLocation] = useLocation();

  useEffect(() => {
    setLocation(to);
  }, [setLocation, to]);

  return null;
}

function LegacyRiderGuideRedirect() {
  return <LegacyResourceRedirect to="/resources/rider" />;
}

function LegacyCaregiverGuideRedirect() {
  return <LegacyResourceRedirect to="/resources/caregiver" />;
}

function LegacyVolunteerGuideRedirect() {
  return <LegacyResourceRedirect to="/resources/volunteer" />;
}

const routeMeta: Record<string, { title: string; description: string }> = {
  "/": {
    title: "Free Trishaw Rides in McFarland, WI | Cycling Without Age McFarland",
    description: "Cycling Without Age McFarland provides free, safe trishaw rides for seniors and people with limited mobility, powered by local volunteers and the Lions Club.",
  },
  "/book": {
    title: "Book a Free Trishaw Ride in McFarland, WI",
    description: "Choose a date and time for a free Cycling Without Age trishaw ride in McFarland, Wisconsin. Rides meet at the McFarland Municipal Building.",
  },
  "/donate": {
    title: "Donate to Free Senior Rides in McFarland, WI",
    description: "Support free trishaw rides for seniors and people with limited mobility in McFarland, Wisconsin. Help keep rides safe, welcoming, and free.",
  },
  "/sponsor": {
    title: "Sponsor Cycling Without Age McFarland",
    description: "Partner with Cycling Without Age McFarland to support free trishaw rides and make a visible difference for seniors and families in our community.",
  },
  "/resources/caregiver": {
    title: "Caregiver Guide to Booking a Trishaw Ride | McFarland",
    description: "A practical guide for McFarland caregivers arranging a free, relaxed Cycling Without Age trishaw ride for a senior or person with limited mobility.",
  },
  "/resources/volunteer": {
    title: "Volunteer Pilot Guide | Cycling Without Age McFarland",
    description: "Learn what it takes to volunteer as a Cycling Without Age trishaw pilot in McFarland, including training, the ride, and how to get started.",
  },
  "/resources/rider": {
    title: "Rider Guide to Free Trishaw Rides | McFarland",
    description: "Learn what to expect on a free Cycling Without Age trishaw ride in McFarland, including comfort, meeting point, booking, and ride support.",
  },
  "/accessibility": {
    title: "Accessibility and Comfort | Cycling Without Age McFarland",
    description: "Learn how Cycling Without Age McFarland helps riders prepare for a comfortable trishaw ride and who to call with accessibility questions.",
  },
  "/resources": {
    title: "Resources | Cycling Without Age McFarland",
    description: "Rider, caregiver, volunteer, accessibility, and comfort resources for Cycling Without Age McFarland.",
  },
  "/about": {
    title: "About Cycling Without Age McFarland",
    description: "Learn about Cycling Without Age McFarland, a McFarland Lions Club-backed chapter providing free trishaw rides for seniors and people with limited mobility.",
  },
  "/contact": {
    title: "Contact Cycling Without Age McFarland",
    description: "Contact Cycling Without Age McFarland about rides, caregiver questions, volunteering, sponsorship, or community partnerships.",
  },
  "/impact": {
    title: "Community Impact | Cycling Without Age McFarland",
    description: "Learn how free Cycling Without Age trishaw rides create fresh air, connection, and joy in McFarland.",
  },
};

const breadcrumbLabels: Record<string, string> = {
  "/": "Home",
  "/book": "Book a Ride",
  "/donate": "Donate",
  "/sponsor": "Sponsor",
  "/resources": "Resources",
  "/resources/rider": "Rider Guide",
  "/resources/caregiver": "Caregiver Guide",
  "/resources/volunteer": "Volunteer Pilot Guide",
  "/accessibility": "Accessibility and Comfort",
  "/about": "About",
  "/contact": "Contact",
  "/impact": "Community Impact",
};

function RouteMetadata() {
  const [path] = useLocation();

  useEffect(() => {
    const normalizedPath = path.replace(/\/+$/, "") || "/";
    const isCommandCenter = normalizedPath.endsWith("/commandcenter");
    const meta = routeMeta[normalizedPath] ?? {
      title: "Cycling Without Age McFarland",
      description: "Free trishaw rides and community connection for seniors and people with limited mobility in McFarland, Wisconsin.",
    };
    const robotsContent = isCommandCenter ? "noindex, nofollow, noarchive" : "index, follow";
    const setMeta = (selector: string, attribute: string, value: string) => {
      const element = document.querySelector(selector) ?? document.head.appendChild(Object.assign(document.createElement("meta"), { }));
      element.setAttribute(attribute, value);
      return element;
    };
    document.title = isCommandCenter ? "Command Center | Cycling Without Age McFarland" : meta.title;
    setMeta('meta[name="description"]', "name", "description").setAttribute("content", meta.description);
    setMeta('meta[name="robots"]', "name", "robots").setAttribute("content", robotsContent);
    setMeta('meta[property="og:title"]', "property", "og:title").setAttribute("content", meta.title);
    setMeta('meta[property="og:description"]', "property", "og:description").setAttribute("content", meta.description);
    setMeta('meta[property="og:url"]', "property", "og:url").setAttribute("content", `https://cwamcfarland.com${normalizedPath === "/" ? "/" : normalizedPath}`);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title").setAttribute("content", meta.title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description").setAttribute("content", meta.description);
    const canonical = document.querySelector('link[rel="canonical"]') ?? document.head.appendChild(Object.assign(document.createElement("link"), { }));
    canonical.setAttribute("rel", "canonical");
    canonical.setAttribute("href", `https://cwamcfarland.com${normalizedPath === "/" ? "/" : normalizedPath}`);
    const schemaId = "cwa-route-schema";
    document.getElementById(schemaId)?.remove();
    if (!isCommandCenter) {
      const schema = document.createElement("script");
      schema.id = schemaId;
      schema.type = "application/ld+json";
      schema.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "NonprofitOrganization",
            "@id": "https://cwamcfarland.com/#organization",
            name: "Cycling Without Age McFarland",
            url: "https://cwamcfarland.com/",
            telephone: "+1-608-579-3356",
            email: "cwamcfarland@gmail.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "5915 Milwaukee St",
              addressLocality: "McFarland",
              addressRegion: "WI",
              postalCode: "53558",
              addressCountry: "US",
            },
            sameAs: ["https://www.facebook.com/cwamcfarland", "https://www.instagram.com/cwamcfarlandwi"],
          },
          {
            "@type": "WebPage",
            "@id": `https://cwamcfarland.com${normalizedPath}#webpage`,
            url: `https://cwamcfarland.com${normalizedPath}`,
            name: meta.title,
            description: meta.description,
            isPartOf: { "@id": "https://cwamcfarland.com/#website" },
            about: { "@id": "https://cwamcfarland.com/#organization" },
          },
          {
            "@type": "WebSite",
            "@id": "https://cwamcfarland.com/#website",
            url: "https://cwamcfarland.com/",
            name: "Cycling Without Age McFarland",
            publisher: { "@id": "https://cwamcfarland.com/#organization" },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://cwamcfarland.com/" },
              ...(normalizedPath === "/"
                ? []
                : normalizedPath.split("/").filter(Boolean).reduce<Array<{ "@type": string; position: number; name: string; item: string }>>((items, segment, index, segments) => {
                    const itemPath = `/${segments.slice(0, index + 1).join("/")}`;
                    const label = breadcrumbLabels[itemPath] ?? segment.replaceAll("-", " ");
                    items.push({ "@type": "ListItem", position: index + 2, name: label, item: `https://cwamcfarland.com${itemPath}` });
                    return items;
                  }, [])),
            ],
          },
        ],
      });
      document.head.appendChild(schema);
    }
  }, [path]);

  return null;
}

function App() {
  useEffect(() => {
    startAnalytics();
    const handleConversionClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest("a");
      if (!link) return;
      const href = link.getAttribute("href") ?? "";
      const conversionEvent = conversionEventForHref(href);
      if (conversionEvent) trackEvent(conversionEvent);
    };
    document.addEventListener("click", handleConversionClick);
    return () => document.removeEventListener("click", handleConversionClick);
  }, []);

  return (
    <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
      <RouteMetadata />
      <Router />
      <MobileBookingBar />
    </WouterRouter>
  );
}

export default App;
